import "./styles.css";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useLangStore from "@/hooks/useLangStore";
import useFetch from "@/hooks/useFetch";
import usePageSize from "@/hooks/usePageSize";
import usePopupStore from "@/hooks/usePopupStore";
import translations from "@/config/translations";
import Table, { type TableColumn } from "@/UI/components/Table";
import TableCell from "@/UI/components/Table/TableCell";
import SidePopup from "@/UI/popups/SidePopup";
import ProductsForm from "@/UI/forms/ProductsForm";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import { Switch } from "@mui/material";

interface ProductCategory {
    _id: string;
    name: string;
    picture: string | null;
    sort: number;
}

interface ProductSize {
    title: string;
    showTitle: boolean;
    price: number;
    discount: { type: "percentage" | "fixed"; amount: number } | null;
}

interface Product {
    _id: string;
    name: string;
    description: string;
    pictures: string[];
    temperature: "hot" | "cold" | null;
    sizes: ProductSize[];
    discount: { type: "percentage" | "fixed"; amount: number } | null;
    category: ProductCategory[];
    sort: number;
    active: boolean;
    hidden: boolean;
}

function parseUrlFilters(searchParams: URLSearchParams): Record<string, string | string[]> {
    const keys = ["search", "category", "temperature", "active", "hidden", "min_price", "max_price"];
    const result: Record<string, string | string[]> = {};
    for (const key of keys) {
        const vals = searchParams.getAll(key);
        if (vals.length === 0) continue;
        const isArray = ["category", "temperature"].includes(key);
        result[key] = isArray ? vals : vals[0];
    }
    return result;
}

export default function ProductsListScreen() {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const { addComp, removeComp } = usePopupStore();
    const [page, setPage] = useState(1);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const { pageSize, setPageSize } = usePageSize();
    const [searchParams] = useSearchParams();

    // ── Products fetch ────────────────────────────────────────────────────────
    const { data, loading, invoke } = useFetch("products", { query: { page: 1, limit: pageSize, ...parseUrlFilters(searchParams) } });

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        invoke({ query: { page, limit: pageSize, ...parseUrlFilters(searchParams) } });
    }, [page]);

    useEffect(() => {
        const handler = () => {
            setPage(1);
            invoke({ query: { page: 1, limit: pageSize, ...parseUrlFilters(searchParams) } });
        };
        window.addEventListener("products:refresh", handler);
        return () => window.removeEventListener("products:refresh", handler);
    }, []);

    // Listen for filter events dispatched from ProductsScreen
    useEffect(() => {
        const handler = (e: CustomEvent) => {
            setPage(1);
            invoke({ query: { page: 1, limit: pageSize, ...e.detail } });
        };
        window.addEventListener("products:filter", handler as EventListener);
        return () => window.removeEventListener("products:filter", handler as EventListener);
    }, [pageSize]);

    const [products, setProducts] = useState<Product[]>([]);
    const total: number = data?.data?.total ?? 0;

    useEffect(() => {
        setProducts(data?.data?.products ?? []);
    }, [data]);

    const handlePageSizeChange = (size: number) => {
        setPage(1);
        setPageSize(size);
        invoke({ query: { page: 1, limit: size, ...parseUrlFilters(searchParams) } });
    };

    // ── Delete fetch ──────────────────────────────────────────────────────────
    const { loading: deleting, status: deleteStatus, invoke: deleteInvoke } = useFetch("products", { method: "DELETE" }, false);

    const wasDeleting = useRef(false);
    useEffect(() => {
        if (deleting) {
            wasDeleting.current = true;
        } else if (wasDeleting.current) {
            wasDeleting.current = false;
            setDeletingId(null);
            if (deleteStatus === 200) invoke({ query: { page, limit: pageSize, ...parseUrlFilters(searchParams) } });
        }
    }, [deleting]);

    const handleDelete = (id: string) => {
        if (deleting) return;
        setDeletingId(id);
        deleteInvoke({ param: id });
    };

    const openDeleteConfirm = (product: Product) => {
        const name = "confirm-delete";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            details: {
                message: t.products.deleteConfirm,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                onConfirm: () => handleDelete(product._id),
            },
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Toggle (active / hidden) ──────────────────────────────────────────────
    const [togglingId, setTogglingId] = useState<string | null>(null);
    const togglePrevRef = useRef<Product | null>(null);
    const { loading: toggling, status: toggleStatus, invoke: toggleInvoke } = useFetch("products", { method: "PUT" }, false);

    const wasToggling = useRef(false);
    useEffect(() => {
        if (toggling) {
            wasToggling.current = true;
        } else if (wasToggling.current) {
            wasToggling.current = false;
            if (toggleStatus !== 200 && togglePrevRef.current) {
                const prev = togglePrevRef.current;
                setProducts((prevList) => prevList.map((p) => (p._id === prev._id ? prev : p)));
            }
            setTogglingId(null);
            togglePrevRef.current = null;
        }
    }, [toggling]);

    const handleToggle = (product: Product, field: "active" | "hidden") => {
        if (togglingId) return;
        togglePrevRef.current = product;
        setTogglingId(product._id);
        setProducts((prev) => prev.map((p) => (p._id === product._id ? { ...p, [field]: !p[field] } : p)));
        toggleInvoke({ param: product._id, data: { [field]: !product[field] } });
    };

    // ── Get-one fetch (edit / duplicate) ──────────────────────────────────────
    const pendingModeRef = useRef<"edit" | "duplicate" | null>(null);
    const { loading: fetchingOne, data: oneData, invoke: fetchOne } = useFetch("products", { method: "GET" }, false);

    const openProductForm = (mode: "edit" | "duplicate", product: any) => {
        const name = mode === "edit" ? "edit-product" : "duplicate-product";
        const title = mode === "edit" ? t.productForm.editTitle : t.productForm.duplicateTitle;
        removeComp(name);
        addComp({
            name,
            Component: SidePopup,
            details: {
                title,
                Content: ProductsForm,
                contentDetails: {
                    productId: mode === "edit" ? product._id : undefined,
                    initialData: product,
                    isDuplicate: mode === "duplicate",
                    onSuccess: () => {
                        invoke({ query: { page: 1, limit: pageSize, ...parseUrlFilters(searchParams) } });
                        setPage(1);
                    },
                },
            },
            place: "end",
            anim: "endSlide",
            cover: "shaded",
            coverClose: true,
        });
    };

    const wasFetchingOne = useRef(false);
    useEffect(() => {
        if (fetchingOne) {
            wasFetchingOne.current = true;
        } else if (wasFetchingOne.current) {
            wasFetchingOne.current = false;
            const product = oneData?.data?.product;
            if (product && pendingModeRef.current) {
                openProductForm(pendingModeRef.current, product);
                pendingModeRef.current = null;
            }
        }
    }, [fetchingOne]);

    const handleEdit = (row: Product) => {
        pendingModeRef.current = "edit";
        fetchOne({ param: row._id });
    };

    const handleDuplicate = (row: Product) => {
        pendingModeRef.current = "duplicate";
        fetchOne({ param: row._id });
    };

    // ── MUI Switch sx ─────────────────────────────────────────────────────────
    const switchSx = {
        width: 34,
        height: 21,
        padding: 0,
        "& .MuiSwitch-switchBase": {
            padding: 0,
            margin: "2.5px",
            transitionDuration: "200ms",
            "&.Mui-checked": {
                transform: "translateX(13px)",
                color: "var(--color-white)",
                "& + .MuiSwitch-track": { backgroundColor: "var(--color-orange)", opacity: 1, border: 0 },
            },
        },
        "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.2)" },
        "& .MuiSwitch-track": { borderRadius: "var(--radius-full)", backgroundColor: "var(--color-gray-01)", opacity: 1 },
    };

    // ── Columns ───────────────────────────────────────────────────────────────
    const columns: TableColumn<Product>[] = [
        {
            key: "pictures",
            header: t.products.col.image,
            width: "76px",
            render: (row) => <TableCell.Gallery images={row.pictures} alt={row.name} />,
        },
        {
            key: "name",
            header: t.products.col.name,
            width: "170px",
            render: (row) => <TableCell.Text value={row.name} maxChars={20} />,
        },
        {
            key: "description",
            header: t.products.col.description,
            width: "200px",
            render: (row) => <TableCell.Description value={row.description || "—"} title={t.products.col.description} maxChars={25} />,
        },
        {
            key: "category",
            header: t.products.col.category,
            width: "180px",
            render: (row) => (
                <div className="products-categories-wrap">
                    {row.category && row.category.length > 0 ? (
                        row.category.slice(0, 2).map((cat) => <TableCell.Badge key={cat._id} label={cat.name} variant="orange" />)
                    ) : (
                        <TableCell.Badge label="—" variant="gray" />
                    )}
                    {row.category && row.category.length > 2 && <span className="products-categories-more">+{row.category.length - 2}</span>}
                </div>
            ),
        },
        {
            key: "temperature",
            header: t.products.col.temperature,
            width: "70px",
            render: (row) =>
                row.temperature ? (
                    <div className="temp-icon-wrap">
                        <img
                            src={row.temperature === "hot" ? "/icons/hot-icon.svg" : "/icons/cold-icon.svg"}
                            alt={t.products.temp[row.temperature]}
                            width={18}
                            height={18}
                        />
                    </div>
                ) : null,
        },
        {
            key: "sizes",
            header: t.products.col.sizes,
            width: "90px",
            render: (row) => <TableCell.Number value={row.sizes?.length ?? 0} />,
        },
        {
            key: "discount",
            header: t.products.col.discount,
            width: "90px",
            render: (row) =>
                row.discount ? (
                    <TableCell.Badge
                        label={row.discount.type === "percentage" ? `${row.discount.amount}%` : `${row.discount.amount}`}
                        variant="orange"
                    />
                ) : (
                    <TableCell.Text value="—" />
                ),
        },
        {
            key: "sort",
            header: t.products.col.sort,
            width: "90px",
            render: (row) => <TableCell.Number value={row.sort} />,
        },
        {
            key: "active",
            header: t.products.col.active,
            width: "80px",
            render: (row) => (
                <Switch
                    checked={row.active}
                    onChange={() => handleToggle(row, "active")}
                    disabled={togglingId === row._id}
                    size="small"
                    sx={switchSx}
                />
            ),
        },
        {
            key: "hidden",
            header: t.products.col.hidden,
            width: "80px",
            render: (row) => (
                <Switch
                    checked={row.hidden}
                    onChange={() => handleToggle(row, "hidden")}
                    disabled={togglingId === row._id}
                    size="small"
                    sx={switchSx}
                />
            ),
        },
        {
            key: "actions",
            header: t.products.col.actions,
            width: "60px",
            render: (row) => (
                <TableCell.Actions
                    disabled={deletingId === row._id || fetchingOne}
                    actions={[
                        { name: "edit", icon: "/icons/edit-icon.svg", handler: () => handleEdit(row) },
                        { name: "duplicate", icon: "/icons/duplicate-icon.svg", handler: () => handleDuplicate(row) },
                        { name: "delete", icon: "/icons/delete-icon.svg", handler: () => openDeleteConfirm(row), danger: true },
                    ]}
                />
            ),
        },
    ];

    return (
        <div className="products-list-screen">
            <Table
                columns={columns}
                data={products}
                loading={loading}
                rowKey="_id"
                pagination={{ page, pageSize, total, onPageChange: setPage, onPageSizeChange: handlePageSizeChange }}
            />
        </div>
    );
}
